import { formatMoneyShort, parseProductionCents } from '@/api/boundaryAdapter';
import type {
  AppointmentDossier,
  AppointmentInsuranceDossier,
  IconKind,
  OpportunityHeadline,
} from '@elva/ui';
import type {
  ActiveFlagWire,
  AppointmentDetails,
  AppointmentDetailsRaw,
  AppointmentHeader,
  AppointmentNote,
  AppointmentNotesResponse,
  AppointmentNotesResponseRaw,
  AppointmentOpportunityWindow,
  AppointmentWire,
  OpportunitySection,
  PatientSummaryWire,
  PlaybookSectionWire,
  PlaybookWire,
  QuickFactsWire,
} from '../api/types';

const EMPTY_OPPORTUNITY_SECTION: OpportunitySection = { tldr: [], steps: [] };

export interface AppointmentDetailsFallback {
  patientName?: string;
  appointmentId?: number;
}

const FLAG_ICON_MAP: Record<string, IconKind> = {
  chart: 'bars',
  alert_triangle: 'alert',
  alert: 'alert',
  sparkle: 'sparkle',
  shield: 'shield',
  dollar: 'dollar',
  calendar: 'calendar',
  heart: 'heart',
  crown: 'crown',
  clipboard: 'clipboard',
};

function formatPatientSince(iso: string): string {
  try {
    return new Date(iso).toLocaleDateString(undefined, { month: 'short', year: 'numeric' });
  } catch {
    return iso;
  }
}

function formatNoteWhen(iso: string): string {
  try {
    return new Date(iso).toLocaleString(undefined, {
      month: 'short',
      day: 'numeric',
      hour: 'numeric',
      minute: '2-digit',
    });
  } catch {
    return iso;
  }
}

function formatAppointmentTime(iso: string | undefined): string {
  if (!iso) return '';
  try {
    return new Date(iso).toLocaleString(undefined, {
      weekday: 'short',
      month: 'short',
      day: 'numeric',
      hour: 'numeric',
      minute: '2-digit',
    });
  } catch {
    return iso;
  }
}

function scoreLabel(value: number | null | undefined): string {
  if (value == null || Number.isNaN(value)) return '—';
  return `${String(Math.round(value)).padStart(2, '0')} / 10`;
}

function normalizeScore(value: number | string | null | undefined): number | null {
  if (value == null || value === '') return null;
  const parsed = typeof value === 'string' ? Number.parseFloat(value) : value;
  if (!Number.isFinite(parsed)) return null;
  return parsed > 10 ? Math.round(parsed / 10) : Math.round(parsed);
}

function genderLabel(gender: string | undefined): string {
  switch (gender?.toUpperCase()) {
    case 'F':
      return 'Female';
    case 'M':
      return 'Male';
    default:
      return gender ?? '';
  }
}

function flagColorToRollup(color: string | undefined): string {
  switch (color?.toLowerCase()) {
    case 'green':
      return 'g';
    case 'yellow':
      return 'y';
    case 'red':
      return 'r';
    default:
      return color ?? '';
  }
}

export function mapFlagIcon(icon: string | undefined): IconKind {
  if (!icon) return 'sparkle';
  return FLAG_ICON_MAP[icon] ?? 'sparkle';
}

function unwrapDetailsRaw(raw: AppointmentDetailsRaw): AppointmentDetailsRaw {
  return raw.details ?? raw;
}

function isStructuredPayload(raw: AppointmentDetailsRaw): boolean {
  return raw.appointment != null || raw.patient_summary != null;
}

function normalizePatientLegacy(
  raw: AppointmentDetailsRaw,
  header: AppointmentDetailsRaw['header'],
  fallback?: AppointmentDetailsFallback,
): AppointmentHeader['patient'] {
  const nested = header?.patient;
  if (nested?.name) {
    return {
      id: nested.id ?? header?.patient_id ?? raw.patient_id ?? fallback?.appointmentId ?? 0,
      name: nested.name,
      sex: nested.sex ?? header?.patient_sex ?? raw.patient_sex ?? '',
      age: nested.age ?? header?.patient_age ?? raw.patient_age ?? 0,
      patient_since: nested.patient_since ?? header?.patient_since ?? raw.patient_since ?? '',
    };
  }

  const name =
    header?.patient_name ??
    raw.patient_name ??
    fallback?.patientName ??
    'Unknown';

  return {
    id: header?.patient_id ?? raw.patient_id ?? fallback?.appointmentId ?? 0,
    name,
    sex: header?.patient_sex ?? raw.patient_sex ?? '',
    age: header?.patient_age ?? raw.patient_age ?? 0,
    patient_since: header?.patient_since ?? raw.patient_since ?? '',
  };
}

function normalizePatientStructured(
  appointment: AppointmentWire | undefined,
  summary: PatientSummaryWire | undefined,
  fallback?: AppointmentDetailsFallback,
): AppointmentHeader['patient'] {
  return {
    id: summary?.id ?? appointment?.patient_id ?? fallback?.appointmentId ?? 0,
    name: summary?.display_name ?? fallback?.patientName ?? 'Unknown',
    sex: genderLabel(summary?.gender),
    age: 0,
    patient_since: summary?.patient_since ?? '',
  };
}

function normalizeHeader(
  raw: AppointmentDetailsRaw,
  fallback?: AppointmentDetailsFallback,
): AppointmentHeader {
  if (isStructuredPayload(raw)) {
    const appointment = raw.appointment;
    const summary = raw.patient_summary;
    return {
      patient: normalizePatientStructured(appointment, summary, fallback),
      status: appointment?.status ?? '',
      is_new_patient: appointment?.is_new_patient ?? false,
      is_vip: summary?.vip_status ?? false,
    };
  }

  const header = raw.header ?? {};
  return {
    patient: normalizePatientLegacy(raw, header, fallback),
    status: header.status ?? raw.status ?? raw.status_label ?? '',
    is_new_patient: header.is_new_patient ?? raw.is_new_patient ?? false,
    is_vip: header.is_vip ?? raw.is_vip ?? false,
  };
}

function normalizeQuickFactsStructured(
  appointment: AppointmentWire | undefined,
  quickFacts: QuickFactsWire | undefined,
): AppointmentDetails['quick_facts'] {
  const production_cents =
    quickFacts?.production_amount_cents ??
    quickFacts?.production_cents ??
    parseProductionCents(quickFacts?.production) ??
    0;

  return {
    time: formatAppointmentTime(appointment?.scheduled_start),
    duration_min: appointment?.duration_minutes ?? quickFacts?.duration_minutes ?? 60,
    provider: appointment?.provider_display_name ?? quickFacts?.provider_name ?? '',
    operatory:
      appointment?.operatory_name ??
      quickFacts?.operatory_name ??
      quickFacts?.operatory_short_name ??
      '',
    kiosk_code: quickFacts?.kiosk_pin ?? quickFacts?.kiosk_code ?? '',
    production_cents,
    ...(appointment?.appointment_reason_label
      ? { procedure: appointment.appointment_reason_label }
      : {}),
  };
}

function normalizeQuickFactsLegacy(raw: AppointmentDetailsRaw): AppointmentDetails['quick_facts'] {
  const qf = raw.quick_facts ?? {};
  const production_cents =
    qf.production_cents ??
    qf.production_amount_cents ??
    parseProductionCents(qf.production) ??
    parseProductionCents(raw.production) ??
    raw.production_cents ??
    0;

  return {
    time: qf.time ?? raw.time ?? raw.start_local ?? raw.start ?? '',
    duration_min:
      qf.duration_minutes ??
      raw.duration_minutes ??
      raw.duration_min ??
      60,
    provider: qf.provider_name ?? raw.provider_name ?? '',
    operatory: qf.operatory_name ?? qf.operatory_short_name ?? raw.operatory_name ?? '',
    kiosk_code: qf.kiosk_code ?? qf.kiosk_pin ?? raw.kiosk_code ?? '',
    production_cents,
  };
}

function normalizeQuickFacts(raw: AppointmentDetailsRaw): AppointmentDetails['quick_facts'] {
  if (isStructuredPayload(raw)) {
    return normalizeQuickFactsStructured(raw.appointment, raw.quick_facts);
  }
  return normalizeQuickFactsLegacy(raw);
}

function activeFlagsToSteps(flags: ActiveFlagWire[] | undefined): OpportunitySection['steps'] {
  return (flags ?? []).map((flag) => ({
    icon: flag.icon ?? 'sparkle',
    headline: flag.label ?? flag.code ?? '',
    source: 'elva',
    detail: flag.detail ?? '',
  }));
}

function formatWireLabel(value: string): string {
  const trimmed = value.trim();
  if (!trimmed || !trimmed.includes('_')) return trimmed;
  return trimmed
    .split('_')
    .filter(Boolean)
    .map((word) => word.charAt(0).toUpperCase() + word.slice(1).toLowerCase())
    .join(' ');
}

function normalizePlaybookSection(section: PlaybookSectionWire | null | undefined): OpportunitySection {
  if (!section) return EMPTY_OPPORTUNITY_SECTION;
  if (section.status === 'not_applicable') {
    return EMPTY_OPPORTUNITY_SECTION;
  }

  const steps = (section.steps ?? section.headlines ?? []).map((step) => ({
    icon: step.icon ?? 'sparkle',
    headline: formatWireLabel(step.headline ?? ''),
    source: step.source ?? 'guideline',
    detail: formatWireLabel(step.detail ?? ''),
  }));
  const tldr = (section.tldr ?? section.summary ?? [])
    .map((line) => formatWireLabel(line))
    .filter(Boolean);

  return {
    tldr,
    steps,
  };
}

function mergeWalkOutSections(playbook: PlaybookWire | undefined): OpportunitySection {
  const preVisit = normalizePlaybookSection(playbook?.walk_out_pre_visit);
  const postAmbient = normalizePlaybookSection(playbook?.walk_out_post_ambient);

  if (preVisit.steps.length === 0 && preVisit.tldr.length === 0) {
    return postAmbient;
  }
  if (postAmbient.steps.length === 0 && postAmbient.tldr.length === 0) {
    return preVisit;
  }

  return {
    tldr: [...preVisit.tldr, ...postAmbient.tldr],
    steps: [...preVisit.steps, ...postAmbient.steps],
  };
}

function normalizeOpportunityWindowStructured(raw: AppointmentDetailsRaw): AppointmentOpportunityWindow {
  const playbook = raw.playbook;
  const flag = raw.flag;

  let walkIn = normalizePlaybookSection(playbook?.walk_in);
  if (walkIn.steps.length === 0 && walkIn.tldr.length === 0) {
    const tldr = flag?.primary_reason ? [flag.primary_reason] : [];
    walkIn = {
      tldr,
      steps: activeFlagsToSteps(flag?.active_flags),
    };
  }

  const producers = raw.patient_producers;
  if (producers?.next_best_action) {
    const action = formatWireLabel(producers.next_best_action);
    if (action) {
      walkIn = {
        ...walkIn,
        tldr: [...walkIn.tldr, action],
      };
    }
  }

  return {
    walk_in: walkIn,
    walk_out: mergeWalkOutSections(playbook),
  };
}

function normalizeOpportunitySection(section?: Partial<OpportunitySection>): OpportunitySection {
  return {
    tldr: section?.tldr ?? [],
    steps: section?.steps ?? [],
  };
}

function normalizeOpportunityWindowLegacy(
  raw?: AppointmentDetailsRaw['opportunity_window'],
): AppointmentOpportunityWindow {
  if (Array.isArray(raw)) {
    return {
      walk_in: normalizeOpportunitySection(raw[0]),
      walk_out: normalizeOpportunitySection(raw[1]),
    };
  }

  const window = raw as Partial<AppointmentOpportunityWindow> & {
    walkIn?: OpportunitySection;
    walkOut?: OpportunitySection;
  };

  return {
    walk_in: normalizeOpportunitySection(window?.walk_in ?? window?.walkIn),
    walk_out: normalizeOpportunitySection(window?.walk_out ?? window?.walkOut),
  };
}

function normalizeOpportunityWindow(raw: AppointmentDetailsRaw): AppointmentOpportunityWindow {
  if (isStructuredPayload(raw)) {
    return normalizeOpportunityWindowStructured(raw);
  }
  return normalizeOpportunityWindowLegacy(raw.opportunity_window);
}

function normalizeInsuranceInsight(
  raw?: AppointmentDetailsRaw['insurance'] | AppointmentDetailsRaw['insurance_insight'],
): AppointmentDetails['insurance_insight'] | undefined {
  if (!raw) return undefined;

  let collectToday = raw.collect_today;
  if (typeof collectToday === 'string') {
    const [amountPart, ...reasonParts] = collectToday.split('—');
    const amountCents = parseProductionCents(amountPart?.replace(/[$,]/g, '').trim()) ?? 0;
    collectToday = {
      amount_cents: amountCents,
      reason: reasonParts.join('—').trim() || collectToday,
    };
  }

  return {
    carrier: raw.carrier ?? '',
    tell_patient: raw.tell_patient ?? '',
    collect_today: collectToday ?? { amount_cents: 0, reason: '' },
    handled: raw.handled ?? [],
    todo: raw.todo ?? [],
  };
}

function normalizeClinicalAlertItem(item: string | { label?: string; code?: string }): string | null {
  if (typeof item === 'string') {
    const trimmed = item.trim();
    return trimmed || null;
  }
  if (item && typeof item === 'object') {
    const label = item.label?.trim();
    if (label) return label;
    const code = item.code?.trim();
    if (code) return code;
  }
  return null;
}

function normalizeClinicalAlertItems(
  items: Array<string | { label?: string }> | undefined,
): string[] {
  return (items ?? [])
    .map(normalizeClinicalAlertItem)
    .filter((item): item is string => item != null);
}

function normalizeClinicalAlerts(raw: AppointmentDetailsRaw): AppointmentDetails['clinical_alerts'] {
  const clinical = raw.clinical_alerts ?? {};
  const mindful = normalizeClinicalAlertItems(clinical.be_mindful_of ?? clinical.be_mindful);

  if (raw.patient_summary?.anxiety_level != null && raw.patient_summary.anxiety_level >= 4) {
    mindful.push(`Anxiety level ${String(raw.patient_summary.anxiety_level)}`);
  }
  if (raw.reliability?.no_show_risk === 'high') {
    mindful.push('Elevated no-show risk');
  }

  return {
    allergies: normalizeClinicalAlertItems(clinical.allergies),
    conditions: normalizeClinicalAlertItems(clinical.conditions),
    medications: normalizeClinicalAlertItems(clinical.medications),
    be_mindful_of: mindful,
  };
}

function normalizePatientContext(raw: AppointmentDetailsRaw): AppointmentDetails['patient_context'] {
  if (isStructuredPayload(raw)) {
    const producers = raw.patient_producers ?? {};
    const starters = producers.conversation_starters;
    const starterText = Array.isArray(starters)
      ? starters.join(' · ')
      : starters ?? '';

    return {
      doctor_note:
        producers.doctor_note_summary ??
        raw.quick_facts?.pms_note ??
        starterText ??
        '',
      last_interaction:
        producers.last_interaction_summary ??
        raw.reliability?.label ??
        '',
      family: [],
    };
  }

  const context = raw.patient_context ?? {};
  return {
    doctor_note: context.doctor_note ?? '',
    last_interaction: context.last_interaction ?? '',
    family: context.family ?? [],
  };
}

function normalizeComfort(raw: AppointmentDetailsRaw): Record<string, string> {
  if (isStructuredPayload(raw)) {
    const prefs = raw.comfort_preferences ?? {};
    const comfort: Record<string, string> = {};

    if (prefs.notes?.trim()) comfort.Notes = prefs.notes.trim();
    if (raw.patient_summary?.anxiety_level != null) {
      comfort.Anxiety = String(raw.patient_summary.anxiety_level);
    }
    if (raw.patient_summary?.life_stage) {
      comfort['Life stage'] = raw.patient_summary.life_stage.replace(/_/g, ' ');
    }
    if (raw.patient_summary?.language_preference) {
      comfort.Language = raw.patient_summary.language_preference.toUpperCase();
    }

    return comfort;
  }

  return raw.comfort ?? {};
}

function normalizeValue(raw: AppointmentDetailsRaw): AppointmentDetails['value'] {
  if (isStructuredPayload(raw)) {
    const value = raw.patient_value ?? {};
    return {
      total_lifetime_cents: value.lifetime_value_cents ?? 0,
      health_score: normalizeScore(value.health_score),
      relationship_score: normalizeScore(value.relationship_score),
      balance_cents: value.outstanding_balance_cents ?? 0,
    };
  }

  const value = raw.value ?? {};
  return {
    total_lifetime_cents: value.total_lifetime_cents ?? 0,
    health_score: normalizeScore(value.health_score),
    relationship_score: normalizeScore(value.relationship_score),
    balance_cents: value.balance_cents ?? 0,
  };
}

function buildDemographics(header: AppointmentHeader, raw: AppointmentDetailsRaw): string {
  if (isStructuredPayload(raw)) {
    const summary = raw.patient_summary;
    return [
      genderLabel(summary?.gender) || header.patient.sex,
      summary?.age_bucket,
      summary?.life_stage?.replace(/_/g, ' '),
      header.patient.patient_since
        ? `Patient since ${formatPatientSince(header.patient.patient_since)}`
        : null,
    ]
      .filter(Boolean)
      .join(' · ');
  }

  const patient = header.patient;
  return [
    patient.sex,
    patient.age ? String(patient.age) : null,
    patient.patient_since
      ? `Patient since ${formatPatientSince(patient.patient_since)}`
      : null,
  ]
    .filter(Boolean)
    .join(' · ');
}

export function adaptAppointmentDetailsResponse(
  raw: AppointmentDetailsRaw,
  fallback?: AppointmentDetailsFallback,
): AppointmentDetails {
  const payload = unwrapDetailsRaw(raw);
  const insuranceInsight =
    normalizeInsuranceInsight(payload.insurance) ??
    normalizeInsuranceInsight(payload.insurance_insight);
  const embeddedNotes = payload.appointment_notes ?? [];

  const header = normalizeHeader(payload, fallback);
  const quick_facts = normalizeQuickFacts(payload);

  return {
    appointment_id:
      payload.appointment?.id ??
      payload.appointment_id ??
      payload.id ??
      fallback?.appointmentId ??
      0,
    rollup_color:
      payload.rollup_color ??
      flagColorToRollup(payload.flag?.color) ??
      '',
    header,
    quick_facts,
    demographics: buildDemographics(header, payload),
    clinical_alerts: normalizeClinicalAlerts(payload),
    opportunity_window: normalizeOpportunityWindow(payload),
    ...(insuranceInsight ? { insurance_insight: insuranceInsight } : {}),
    patient_context: normalizePatientContext(payload),
    comfort: normalizeComfort(payload),
    value: normalizeValue(payload),
    notes_count: payload.notes_count ?? embeddedNotes.length,
    ...(embeddedNotes.length > 0 ? { detail_notes: embeddedNotes } : {}),
  };
}

export function adaptAppointmentNotesResponse(
  raw: AppointmentNotesResponseRaw | AppointmentNote[],
): AppointmentNotesResponse {
  if (Array.isArray(raw)) return { notes: raw };
  return { notes: raw.notes ?? [] };
}

function mapOpportunitySection(section: OpportunitySection): AppointmentDossier['opportunityWindow']['walkin'] {
  return {
    summary: section.tldr ?? [],
    headlines: (section.steps ?? []).map(
      (step, index): OpportunityHeadline => ({
        id: `step-${String(index)}`,
        icon: mapFlagIcon(step.icon),
        source: step.source === 'elva' ? 'elva' : 'guideline',
        headline: step.headline,
        detail: step.detail,
      }),
    ),
  };
}

function mapOpportunityWindow(
  window: AppointmentOpportunityWindow,
): AppointmentDossier['opportunityWindow'] {
  return {
    walkin: mapOpportunitySection(window.walk_in ?? EMPTY_OPPORTUNITY_SECTION),
    walkout: mapOpportunitySection(window.walk_out ?? EMPTY_OPPORTUNITY_SECTION),
  };
}

function mergeNotes(
  detailNotes: AppointmentNote[] | undefined,
  apiNotes: AppointmentNote[],
): AppointmentNote[] {
  const byId = new Map<number, AppointmentNote>();
  (detailNotes ?? []).forEach((note) => {
    byId.set(note.id, note);
  });
  apiNotes.forEach((note) => {
    byId.set(note.id, note);
  });
  return [...byId.values()].sort(
    (a, b) => new Date(b.created_at).getTime() - new Date(a.created_at).getTime(),
  );
}

export function adaptAppointmentDossier(
  details: AppointmentDetails,
  notes: AppointmentNote[] = [],
): AppointmentDossier {
  const { header, quick_facts, clinical_alerts, insurance_insight, patient_context, comfort, value } =
    details;

  const collectToday = insurance_insight?.collect_today;
  const collectTodayLabel = collectToday
    ? `${formatMoneyShort(collectToday.amount_cents)} — ${collectToday.reason}`
    : '—';

  const allNotes = mergeNotes(details.detail_notes, notes);

  return {
    demographics: details.demographics || 'Unknown',
    operatory: quick_facts?.operatory ?? '',
    kioskCode: quick_facts?.kiosk_code ?? '',
    clinicalAlerts: {
      allergies: clinical_alerts?.allergies ?? [],
      conditions: clinical_alerts?.conditions ?? [],
      medications: clinical_alerts?.medications ?? [],
      mindful: clinical_alerts?.be_mindful_of ?? [],
    },
    opportunityWindow: mapOpportunityWindow(details.opportunity_window),
    insurance: {
      planLabel: insurance_insight?.carrier ?? 'Insurance',
      tellPatient: insurance_insight?.tell_patient ?? '',
      collectToday: collectTodayLabel,
      handled: insurance_insight?.handled ?? [],
      todo: insurance_insight?.todo ?? [],
    },
    doctorsNote: patient_context?.doctor_note ?? '',
    lastInteraction: patient_context?.last_interaction ?? '',
    family: (patient_context?.family ?? []).map((member) => ({
      name: member.name,
      rel: member.relationship,
      status: member.status,
      overdue: /overdue/i.test(member.status),
    })),
    comfort: Object.entries(comfort ?? {}).map(([label, val]) => ({
      label,
      value: val,
    })),
    patientValue: [
      {
        label: 'Total value',
        value:
          (value?.total_lifetime_cents ?? 0) > 0
            ? formatMoneyShort(value.total_lifetime_cents)
            : '—',
      },
      { label: 'Health score', value: scoreLabel(value?.health_score) },
      { label: 'Relationship', value: scoreLabel(value?.relationship_score) },
      {
        label: 'Balance',
        value: formatMoneyShort(value?.balance_cents ?? 0),
        tone: (value?.balance_cents ?? 0) > 0 ? 'warning' : 'green',
      },
    ],
    notes: allNotes.map((note) => ({
      author: note.visibility ?? 'Staff',
      when: formatNoteWhen(note.created_at),
      text: note.note_text,
    })),
  };
}

export function adaptAppointmentInsurance(details: AppointmentDetails): AppointmentInsuranceDossier {
  const insight = details.insurance_insight;
  const window = mapOpportunityWindow(details.opportunity_window);

  const seen = new Set<string>();
  const opportunities = [...window.walkin.headlines, ...window.walkout.headlines]
    .filter((h) => {
      if (seen.has(h.headline)) return false;
      seen.add(h.headline);
      return true;
    })
    .map((h) => ({
      id: h.id,
      icon: h.icon,
      accent: h.source === 'elva' ? 'var(--gold)' : 'var(--ai-3)',
      title: h.headline,
      description: h.detail,
    }));

  return {
    opportunities,
    ...(insight
      ? {
          estimate: {
            carrier: insight.carrier,
            patientToday: insight.collect_today.amount_cents / 100,
            ...(insight.collect_today.reason ? { patientNote: insight.collect_today.reason } : {}),
            procedures: [],
          },
        }
      : {}),
    family: (details.patient_context?.family ?? []).map((member) => {
      const overdue = /overdue/i.test(member.status);
      return {
        id: member.name,
        name: member.name,
        relation: member.relationship,
        recallLabel: member.status,
        recallStatus: overdue ? ('overdue' as const) : ('on_schedule' as const),
        recallNote: '',
      };
    }),
  };
}
